import { Group, type Organization } from "@entities";
import {
  GROUP_VOLUNTEER_REGION_LABELS,
  GROUP_VOLUNTEER_REGIONS,
  type GroupVolunteerRegion,
} from "@shared/helpers/group-volunteer-regions";
import { Box, Button, Checkbox, FormControlLabel, TextField } from "@mui/material";
import { DateCalendar, DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { heIL } from "@mui/x-date-pickers/locales";
import { type HyperTableFilter, createPhoneNumberFilter } from "@shared/frontend/hyper-table";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { TeamsSelect } from "../../../components/teams-select";

export const getFilters = (currOrganization: Organization): HyperTableFilter<Group>[] => {
  const statuses = currOrganization.status || []; // Extract statuses from currOrganization

  return [
    { fieldName: "name", id: "name" },
    { fieldName: "size", id: "size", caption: "גודל קבוצה" },
    {
      id: "createdAt",
      icon: "date",
      caption: "תאריך יצירה",
      renderFilter: ({ onSave, meta }) => {
        const start = meta.startDate ? dayjs(meta.startDate as string) : null;
        const end = meta.endDate ? dayjs(meta.endDate as string) : null;
        const build = (nextStart: dayjs.Dayjs | null, nextEnd: dayjs.Dayjs | null) => {
          if (!nextStart?.isValid() && !nextEnd?.isValid()) return {};
          return Group.hyperFilter({
            createdAt: {
              ...(nextStart?.isValid()
                ? { $gte: nextStart.startOf("day").toDate() }
                : {}),
              ...(nextEnd?.isValid()
                ? { $lte: nextEnd.endOf("day").toDate() }
                : {}),
            },
          });
        };
        return (
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            localeText={heIL.components.MuiLocalizationProvider.defaultProps.localeText}
            adapterLocale="he"
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, p: 1 }}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <DatePicker
                  label="מתאריך"
                  value={start?.isValid() ? start : null}
                  format="DD/MM/YYYY"
                  onChange={(value) => {
                    const nextStart = value ? dayjs(value) : null;
                    onSave({
                      value: build(nextStart, end?.isValid() ? end : null),
                      meta: {
                        startDate: nextStart?.toISOString() ?? null,
                        endDate: end?.isValid() ? end.toISOString() : null,
                      },
                    });
                  }}
                  slotProps={{ textField: { size: "small" } }}
                />
                <DatePicker
                  label="עד תאריך"
                  value={end?.isValid() ? end : null}
                  format="DD/MM/YYYY"
                  onChange={(value) => {
                    const nextEnd = value ? dayjs(value) : null;
                    onSave({
                      value: build(start?.isValid() ? start : null, nextEnd),
                      meta: {
                        startDate: start?.isValid() ? start.toISOString() : null,
                        endDate: nextEnd?.toISOString() ?? null,
                      },
                    });
                  }}
                  slotProps={{ textField: { size: "small" } }}
                />
              </Box>
            </Box>
          </LocalizationProvider>
        );
      },
    },
    { fieldName: "description", id: "description" },
    { fieldName: "comments", id: "comments" },
    { fieldName: "type", id: "type" },
    { fieldName: "admin.name", caption: "מוביל קבוצה", id: "adminName" },
    createPhoneNumberFilter(Group, {
      fieldName: "admin.phoneNumber",
      caption: "טלפון מוביל קבוצה",
      id: "adminPhoneNumber",
    }),
    {
      id: "participant",
      icon: "date",
      caption: "משתתפות במשימה בתאריך",
      renderFilter: ({ onSave, meta }) => {
        const dayjsValue =
          typeof meta.value === "string"
            ? dayjs(new Date(meta.value))
            : meta.value;
        return (
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            localeText={
              heIL.components.MuiLocalizationProvider.defaultProps.localeText
            }
            adapterLocale="he"
          >
            <DateCalendar
              timezone="UTC"
              value={dayjsValue as Dayjs & { $d: Date; }}
              onChange={(value) => {
                onSave({
                  value: Group.filterGroupsByEventDate({ date: value }),
                  meta: {
                    value,
                  },
                });
              }}
            />
          </LocalizationProvider>
        );
      },
    },
    {
      id: "teams",
      icon: "literal",
      caption: "פרויקטים",
      renderFilter: ({ onSave, meta }) => {
        const { teams } = meta;
        return (
          <TeamsSelect
            id="opportunities-table"
            value={teams as string[]}
            onChange={(teams) =>
              onSave({
                value: teams.length
                  ? Group.hyperFilter({
                    team: {
                      $is: {
                        id: { $in: teams as string[] },
                      },
                    },
                  })
                  : {},
                meta: { teams },
              })
            }
          />
        );
      },
    },
    {
      id: "preferred-date-filter",
      caption: "מועד מועדף בתאריך",
      icon: "date",
      renderFilter: ({ onSave, meta }) => {
        const dayjsValue =
          typeof meta.value === "string"
            ? dayjs(new Date(meta.value))
            : meta.value;
        return (
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            localeText={
              heIL.components.MuiLocalizationProvider.defaultProps.localeText
            }
            adapterLocale="he"
          >
            <DateCalendar
              timezone="UTC"
              value={dayjsValue as Dayjs & { $d: Date; }}
              onChange={(value) => {
                const startOfDay = new Date((value as Dayjs).toDate());
                startOfDay.setHours(0, 0, 0, 0); // Start of the day
                const endOfDay = new Date((value as Dayjs).toDate());
                endOfDay.setHours(23, 59, 59, 999); // End of the day
                onSave({
                  value: {
                    $and: [
                      { preferredStartDate: { $lte: startOfDay } },
                      { preferredEndDate: { $gte: endOfDay } },
                    ],
                  },
                  meta: {
                    value,
                  },
                });
              }}
            />
          </LocalizationProvider>
        );
      },
    },
    {
      id: "status",
      icon: "literal",
      caption: "סטטוס",
      renderFilter: ({ onSave, meta }) => {
        const [metaStatuses, setMetaStatuses] = useState<string[]>(meta.statuses as string[] || []); // State for selected statuses
        const [noStatus, setNoStatus] = useState<boolean>(meta.noStatus as boolean || false); // State for "no status" checkbox

        const handleSave = () => {
          const conditions = [];
          if (metaStatuses.length) {
            conditions.push({
              status: {
                $is: {
                  id: { $in: metaStatuses.map((id) => statuses.find((status) => status.id === id)?.id) },
                },
              },
            });
          }
          if (noStatus) {
            conditions.push({ status: { $is: null } });
          }

          onSave({
            value: conditions.length
              ? Group.hyperFilter({ $or: conditions }) // Combine conditions with $or
              : {}, // Clear filter if no conditions
            meta: { statuses: metaStatuses, noStatus },
          });
        };

        return (
          <div
            style={{
              padding: "10px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={noStatus} // Check if noStatus is selected
                  onChange={(e) => setNoStatus(e.target.checked)} // Update state
                />
              }
              label="ללא סטטוס" // Label for opportunities without a status
            />
            {statuses
              ?.filter((status) => status.type === "GROUP") // Filter by type "GROUP"
              .map((status) => (
                <FormControlLabel
                  key={status.id}
                  control={
                    <Checkbox
                      checked={metaStatuses.includes(status.id)} // Check if ID is in metaStatuses
                      onChange={(e) => {
                        setMetaStatuses((prev) =>
                          e.target.checked
                            ? [...prev, status.id] // Add ID if checked
                            : prev.filter((id) => id !== status.id) // Remove ID if unchecked
                        );
                      }}
                    />
                  }
                  label={status.title}
                />
              ))}
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              style={{ marginTop: "10px" }}
            >
              שמירה
            </Button>
          </div>
        );
      },
    },
    {
      id: "has-registered-opportunities",
      icon: "literal",
      caption: "רשומות למשימות",
      renderFilter: ({ onSave, meta }) => {
        const [hasRegistered, setHasRegistered] = useState<boolean>(meta.hasRegistered as boolean || false);
        const [hasNoRegistered, setHasNoRegistered] = useState<boolean>(meta.hasNoRegistered as boolean || false);

        const handleSave = () => {
          const conditions = [];
          if (hasRegistered) {
            conditions.push({
              opportunities: {
                $some: {}
              }
            });
          }
          if (hasNoRegistered) {
            conditions.push({
              opportunities: {
                $none: {}
              }
            });
          }

          onSave({
            value: conditions.length
              ? Group.hyperFilter({ $or: conditions })
              : {},
            meta: { hasRegistered, hasNoRegistered },
          });
        };

        return (
          <div
            style={{
              padding: "10px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasRegistered}
                  onChange={(e) => setHasRegistered(e.target.checked)}
                />
              }
              label="רשומים למשימות"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasNoRegistered}
                  onChange={(e) => setHasNoRegistered(e.target.checked)}
                />
              }
              label="לא רשומים למשימות"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              style={{ marginTop: "10px" }}
            >
              שמירה
            </Button>
          </div>
        );
      },
    },
    {
      id: "preferred-volunteer-regions",
      icon: "literal",
      caption: "אזור התנדבות מועדף",
      renderFilter: ({ onSave, meta }) => {
        const metaRegions =
          (meta.regions as GroupVolunteerRegion[] | undefined) ?? [];
        const [selectedRegions, setSelectedRegions] =
          useState<GroupVolunteerRegion[]>(metaRegions);

        const handleSave = () => {
          onSave({
            value: selectedRegions.length
              ? Group.filterByPreferredVolunteerRegions({
                  regions: selectedRegions,
                })
              : {},
            meta: { regions: selectedRegions },
          });
        };

        return (
          <div
            style={{
              padding: "10px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            {GROUP_VOLUNTEER_REGIONS.map((region) => (
              <FormControlLabel
                key={region}
                control={
                  <Checkbox
                    checked={selectedRegions.includes(region)}
                    onChange={(e) => {
                      setSelectedRegions((prev) =>
                        e.target.checked
                          ? [...prev, region]
                          : prev.filter((r) => r !== region),
                      );
                    }}
                  />
                }
                label={GROUP_VOLUNTEER_REGION_LABELS[region]}
              />
            ))}
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              style={{ marginTop: "10px" }}
            >
              שמירה
            </Button>
          </div>
        );
      },
    },
    {
      id: "has-interested-opportunities",
      icon: "literal",
      caption: "מתעניינות במשימות",
      renderFilter: ({ onSave, meta }) => {
        const [hasInterested, setHasInterested] = useState<boolean>(meta.hasInterested as boolean || false);
        const [hasNoInterested, setHasNoInterested] = useState<boolean>(meta.hasNoInterested as boolean || false);

        const handleSave = () => {
          onSave({
            value: Group.hyperFilter({
              interestedOpportunities: {
                $some: {}
              }
            }),
            meta: { hasInterested, hasNoInterested },
          });
        };

        return (
          <div
            style={{
              padding: "10px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasInterested}
                  onChange={(e) => setHasInterested(e.target.checked)}
                />
              }
              label="התעניינו במשימה"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              style={{ marginTop: "10px" }}
            >
              שמירה
            </Button>
          </div>
        );
      },
    },
  ];
};

export default getFilters;
